// logo-dark.png is logo.png with the white letters in #3F4043, so it shows on
// white paper (logo.png is made for the blue top bar).
import logoUrl from "../../../../assets/logo/logo-dark.png";
import {
  buildAuctionReport,
  formatDateTime,
  noEndText,
  toNumber,
} from "./auctionReportData";

// A4 landscape, in millimetres
const PAGE_WIDTH = 297;
const PAGE_HEIGHT = 210;
const MARGIN = 10;
const FOOTER_SPACE = 16;
const LOGO_RATIO = 1577 / 425; // width / height of assets/logo/logo-dark.png

// Plain black and white, so it photocopies well
const TABLE_STYLES = {
  theme: "grid",
  styles: {
    font: "helvetica",
    fontSize: 7,
    cellPadding: 1,
    textColor: 0,
    lineColor: 120,
    lineWidth: 0.1,
    valign: "top",
    overflow: "linebreak",
  },
  headStyles: { fillColor: 255, textColor: 0, fontStyle: "bold" },
  footStyles: { fillColor: 255, textColor: 0, fontStyle: "bold" },
};

// Lot table column widths; together they fill the 277 mm between margins.
const LOT_COLUMN_WIDTHS = {
  sr: 7,
  lotNumber: 10,
  description: 47,
  quantity: 10,
  unit: 10,
  startPrice: 17,
  step: 16,
  startAt: 20,
  endAt: 20,
  invited: 12,
  biddersBid: 12,
  bids: 10,
  leadBidder: 24,
  leadBid: 17,
  leadValue: 20,
  otherBids: 25,
};

const amountFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const amountText = (value) => {
  const number = toNumber(value);
  return number === null ? "" : amountFormat.format(number);
};

const numberText = (value) => {
  const number = toNumber(value);
  return number === null ? "" : String(number);
};

const lotCellText = (column, row, report) => {
  const value = row[column.key];
  switch (column.kind) {
    case "number":
      return numberText(value);
    case "amount":
      return amountText(value);
    case "date":
      return value
        ? formatDateTime(value, report.timeZone)
        : noEndText(report.eventFormat);
    default:
      return value === null || value === undefined ? "" : String(value);
  }
};

const isRightAligned = (kind) => kind === "number" || kind === "amount";

const drawLabelRow = (doc, y, label, value, valueFontSize) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(label, MARGIN, y);
  doc.setFontSize(valueFontSize);
  doc.text(value, MARGIN + 25, y);
};

// Footer on every page: Generated │ Powered by [logo] │ Created by · Page x of y
const drawFooters = (doc, report, logo) => {
  const pageCount = doc.getNumberOfPages();
  const y = PAGE_HEIGHT - 8;

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(120);
    doc.setLineWidth(0.1);
    doc.line(MARGIN, y - 5, PAGE_WIDTH - MARGIN, y - 5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(0);
    doc.text(`Generated: ${report.generatedText}`, MARGIN, y);

    const poweredBy = "Powered by";
    const logoHeight = 4;
    const logoWidth = logo ? logoHeight * LOGO_RATIO : 0;
    const poweredByWidth = doc.getTextWidth(`${poweredBy} `);
    const blockWidth = poweredByWidth + (logo ? logoWidth : 0);
    const blockX = (PAGE_WIDTH - blockWidth) / 2;
    if (logo) {
      doc.text(poweredBy, blockX, y);
      doc.addImage(
        logo,
        "PNG",
        blockX + poweredByWidth,
        y - logoHeight + 0.8,
        logoWidth,
        logoHeight,
        "v3bid-logo", // stored once, reused on every page
        "FAST" // compressed: without it the logo alone makes the file ~2.7 MB
      );
    } else {
      doc.text(`${poweredBy} V3bid.com`, PAGE_WIDTH / 2, y, {
        align: "center",
      });
    }

    doc.text(
      `Created by: ${report.createdBy}     Page ${page} of ${pageCount}`,
      PAGE_WIDTH - MARGIN,
      y,
      { align: "right" }
    );
  }
};

// Builds the Lots Listing PDF. jsPDF and autoTable are passed in so the
// libraries are only downloaded when someone clicks the PDF icon.
export const buildLotsListingPdf = ({ jsPDF, autoTable, report, logo }) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const { counts, zoneAbbr } = report;

  let y = 15;
  drawLabelRow(doc, y, "Lot Owner", report.ownerOfLots, 14);
  y += 7;
  drawLabelRow(doc, y, "Auction:", report.auctionTitle, 11);
  if (report.description) {
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Description:", MARGIN, y);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(
      report.description,
      PAGE_WIDTH - 2 * MARGIN - 25
    );
    doc.text(lines, MARGIN + 25, y);
    y += (lines.length - 1) * 4;
  }
  y += 5;

  // Summary: two small tables side by side, as in the draft
  autoTable(doc, {
    ...TABLE_STYLES,
    startY: y,
    margin: { left: MARGIN },
    tableWidth: 125,
    styles: { ...TABLE_STYLES.styles, fontSize: 8 },
    head: [
      [
        "Auction Type & ID",
        `Start Date & Time (${zoneAbbr})`,
        `End Date & Time (${zoneAbbr})`,
      ],
    ],
    body: [
      [
        report.typeText,
        {
          content: formatDateTime(report.startAt, report.timeZone),
          rowSpan: 2,
        },
        {
          content: formatDateTime(
            report.endAt,
            report.timeZone,
            report.eventFormat
          ),
          rowSpan: 2,
        },
      ],
      [report.eventNumber],
    ],
    columnStyles: { 0: { cellWidth: 55 } },
  });
  const summaryBottom = doc.lastAutoTable.finalY;

  autoTable(doc, {
    ...TABLE_STYLES,
    startY: y,
    margin: { left: MARGIN + 135 },
    tableWidth: 80,
    styles: { ...TABLE_STYLES.styles, fontSize: 8 },
    head: [["", "Total", "Bid", "No bid"]],
    body: [
      ["Lots", counts.lots, counts.lotsWithBid, counts.lotsNoBid],
      [
        "Invited bidders",
        counts.invited,
        counts.biddersWhoBid,
        counts.invitedNoBid,
      ],
    ].map((row) => row.map(String)),
    columnStyles: {
      0: { cellWidth: 35 },
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right" },
    },
  });
  y = Math.max(summaryBottom, doc.lastAutoTable.finalY) + 6;

  // Lot table: the header repeats on every page, the total row is on the last
  const columns = report.lotColumns;
  const totalRow = columns.map(({ key }) => {
    if (key === "description") return "Total";
    if (key === "leadValue") return amountText(report.totalLeadValue);
    return "";
  });

  autoTable(doc, {
    ...TABLE_STYLES,
    startY: y,
    margin: { left: MARGIN, right: MARGIN, top: 14, bottom: FOOTER_SPACE },
    head: [columns.map(({ label }) => label)],
    body: report.lotRows.map((row) =>
      columns.map((column) => lotCellText(column, row, report))
    ),
    foot: [totalRow],
    showFoot: "lastPage",
    columnStyles: Object.fromEntries(
      columns.map((column, index) => [
        index,
        {
          cellWidth: LOT_COLUMN_WIDTHS[column.key],
          halign: isRightAligned(column.kind) ? "right" : "left",
        },
      ])
    ),
    didDrawPage: ({ pageNumber }) => {
      if (pageNumber === 1) return;
      // a short reminder of which auction this is, on pages 2 and later
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(0);
      doc.text(
        `${report.auctionTitle} · ${report.eventNumber}`,
        MARGIN,
        MARGIN
      );
    },
  });

  drawFooters(doc, report, logo);
  return doc;
};

const loadLogo = async () => {
  try {
    const blob = await (await fetch(logoUrl)).blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null; // the footer then says "Powered by V3bid.com" in text
  }
};

export const exportLotsListingToPDF = async ({ eventData, lots, zone }) => {
  const [{ jsPDF }, { autoTable }, logo] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
    loadLogo(),
  ]);
  const report = buildAuctionReport({ eventData, lots, zone });
  const doc = buildLotsListingPdf({ jsPDF, autoTable, report, logo });
  doc.save(`${report.fileBase}-lots.pdf`);
};
